<script lang="ts">
	import { _ } from 'svelte-i18n';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	
	const lastUpdated = 'December 2024';
	const effectiveDate = 'December 1, 2024';
</script>

<svelte:head>
	<title>{$_('pages.termsOfUse.meta.title')} - Heyweek</title>
	<meta name="description" content={$_('pages.termsOfUse.meta.description')} />
	<meta property="og:title" content="{$_('pages.termsOfUse.meta.title')} - Heyweek" />
	<meta property="og:description" content={$_('pages.termsOfUse.meta.description')} />
</svelte:head>

<Header />

<main class="legal-page">
	<div class="container">
		<div class="legal-header">
			<h1 class="legal-title">{$_('pages.termsOfUse.title')}</h1>
			<p class="legal-meta">{$_('pages.termsOfUse.lastUpdated')}: {lastUpdated}</p>
		</div>
		
		<div class="legal-content">
			<section class="legal-section">
				<p class="intro">
					Welcome to Heyweek! These Terms of Use ("Terms") govern your access to and use of our website, applications, and services (collectively, the "Services") provided by Heyweek ("we", "us", or "our").
				</p>
				<p>
					By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Services.
				</p>
			</section>

			<section class="legal-section">
				<h2>1. Acceptance of Terms</h2>
				<p>
					By creating an account or using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms, as well as our Privacy Policy. These Terms apply to all visitors, users, and others who access or use our Services.
				</p>
			</section>

			<section class="legal-section">
				<h2>2. Eligibility</h2>
				<p>
					To use our Services, you must:
				</p>
				<ul>
					<li>Be at least 17 years old</li>
					<li>Have the legal capacity to enter into a binding agreement</li>
					<li>Not be prohibited from using our Services under applicable laws</li>
					<li>Provide accurate and complete registration information</li>
				</ul>
				<p>
					By using our Services, you represent and warrant that you meet all of these requirements.
				</p>
			</section>

			<section class="legal-section">
				<h2>3. Account Registration and Security</h2>
				<p>
					When you create an account with us, you must:
				</p>
				<ul>
					<li>Provide accurate, current, and complete information</li>
					<li>Maintain and promptly update your account information</li>
					<li>Maintain the security of your password and account</li>
					<li>Accept responsibility for all activities that occur under your account</li>
					<li>Immediately notify us of any unauthorized use of your account</li>
				</ul>
				<p>
					You may not use another person's account without permission. We reserve the right to suspend or terminate accounts that violate these Terms.
				</p>
			</section>

			<section class="legal-section">
				<h2>4. Use of Services</h2>
				
				<h3>Permitted Use</h3>
				<p>
					You may use our Services for lawful purposes only. You agree to use the Services in accordance with these Terms and all applicable laws and regulations.
				</p>

				<h3>Prohibited Use</h3>
				<p>
					You agree not to:
				</p>
				<ul>
					<li>Use the Services for any illegal or unauthorized purpose</li>
					<li>Violate any laws in your jurisdiction</li>
					<li>Transmit any viruses, malware, or malicious code</li>
					<li>Attempt to gain unauthorized access to any portion of the Services</li>
					<li>Interfere with or disrupt the Services or servers</li>
					<li>Collect or harvest any personally identifiable information from the Services</li>
					<li>Use the Services to send spam or unsolicited messages</li>
					<li>Impersonate any person or entity</li>
					<li>Reverse engineer or attempt to extract the source code of the Services</li>
					<li>Use automated systems or software to extract data from the Services</li>
				</ul>
			</section>

			<section class="legal-section">
				<h2>5. User-Generated Content</h2>
				
				<h3>Your Content</h3>
				<p>
					Our Services allow you to post, link, store, share and otherwise make available certain information, text, graphics, or other material ("Content"). You are responsible for the Content that you post to the Services, including its legality, reliability, and appropriateness.
				</p>

				<h3>Rights to Your Content</h3>
				<p>
					You retain ownership of any Content you submit, post, or display on or through the Services. By posting Content, you grant us a worldwide, non-exclusive, royalty-free license to use, modify, reproduce, and distribute your Content in connection with operating and providing the Services.
				</p>

				<h3>Prohibited Content</h3>
				<p>
					You may not post Content that:
				</p>
				<ul>
					<li>Is unlawful, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable</li>
					<li>Violates the privacy or publicity rights of any third party</li>
					<li>Contains discriminatory content based on race, sex, religion, nationality, disability, sexual orientation, or age</li>
					<li>Infringes any patent, trademark, copyright, or other proprietary rights</li>
					<li>Contains false or misleading information</li>
					<li>Promotes illegal activities or self-harm</li>
				</ul>
			</section>

			<section class="legal-section">
				<h2>6. Intellectual Property</h2>
				<p>
					The Services and all content, features, and functionality (including but not limited to all information, software, text, displays, images, video, and audio) are owned by Heyweek, its licensors, or other providers of such material and are protected by intellectual property laws.
				</p>
				<p>
					The Heyweek name, logo, and all related names, logos, product and service names, designs, and slogans are trademarks of Heyweek. You must not use such marks without our prior written permission.
				</p>
			</section>

			<section class="legal-section">
				<h2>7. Payment and Billing</h2>
				<p>
					<strong>Subscription Plans:</strong> Certain features of our Services require a paid subscription. By selecting a subscription plan, you agree to pay the applicable fees.
				</p>
				<p>
					<strong>Billing:</strong> Subscription fees are billed in advance on a monthly or annual basis and are non-refundable except as required by law or as set forth in these Terms.
				</p>
				<p>
					<strong>Auto-Renewal:</strong> Your subscription will automatically renew at the end of each billing period unless you cancel it before the renewal date.
				</p>
				<p>
					<strong>Price Changes:</strong> We may change our subscription fees from time to time. We will provide you with advance notice of any price changes.
				</p>
				<p>
					<strong>Refunds:</strong> We offer a 30-day money-back guarantee for new subscribers. After this period, payments are non-refundable.
				</p>
			</section>

			<section class="legal-section">
				<h2>8. Termination</h2>
				<p>
					We may terminate or suspend your account and access to the Services immediately, without prior notice or liability, for any reason, including if you breach these Terms.
				</p>
				<p>
					You may terminate your account at any time by contacting us at hello@heyweek.com. Upon termination, your right to use the Services will immediately cease.
				</p>
				<p>
					All provisions of these Terms which by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
				</p>
			</section>

			<section class="legal-section">
				<h2>9. Disclaimers</h2>
				<p class="note">
					THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
				</p>
			</section>

			<section class="legal-section">
				<h2>10. Limitation of Liability</h2>
				<p class="note">
					TO THE MAXIMUM EXTENT PERMITTED BY LAW, HEYWEEK SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICES.
				</p>
				<p>
					In no event shall our total liability exceed the amount paid by you to us in the twelve (12) months preceding the claim.
				</p>
			</section>

			<section class="legal-section">
				<h2>11. Indemnification</h2>
				<p>
					You agree to defend, indemnify, and hold harmless Heyweek and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses arising out of or in any way connected with:
				</p>
				<ul>
					<li>Your access to or use of the Services</li>
					<li>Your violation of these Terms</li>
					<li>Your violation of any third-party rights</li>
					<li>Any Content you post or share through the Services</li>
				</ul>
			</section>

			<section class="legal-section">
				<h2>12. Governing Law and Jurisdiction</h2>
				<p>
					These Terms shall be governed by and construed in accordance with the laws of Sweden, without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of the Services shall be resolved in the courts of Sweden.
				</p>
			</section>

			<section class="legal-section">
				<h2>13. Privacy and Data Protection</h2>
				<p>
					Your use of the Services is also governed by our Privacy Policy. By using the Services, you consent to the collection and use of your information as described in our Privacy Policy.
				</p>
				<p>
					<strong>Data Location:</strong> Your data is stored on servers located in the European Union. By using our Services from outside the EU, you consent to the transfer of your data to the EU.
				</p>
			</section>

			<section class="legal-section">
				<h2>14. Modifications to Terms</h2>
				<p>
					We reserve the right to modify or replace these Terms at any time. If we make material changes, we will notify you by email or by posting a notice on our Services prior to the changes taking effect.
				</p>
				<p>
					Your continued use of the Services after any such changes constitutes your acceptance of the new Terms.
				</p>
			</section>

			<section class="legal-section">
				<h2>15. Miscellaneous</h2>
				
				<h3>Entire Agreement</h3>
				<p>
					These Terms, together with our Privacy Policy, constitute the entire agreement between you and Heyweek regarding the use of the Services.
				</p>

				<h3>Waiver</h3>
				<p>
					Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
				</p>

				<h3>Severability</h3>
				<p>
					If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions will continue in full force and effect.
				</p>

				<h3>Assignment</h3>
				<p>
					You may not assign or transfer these Terms without our prior written consent. We may assign our rights and obligations under these Terms without restriction.
				</p>

				<h3>No Third-Party Beneficiaries</h3>
				<p>
					These Terms do not create any third-party beneficiary rights.
				</p>
			</section>

			<section class="legal-section">
				<h2>16. Contact Information</h2>
				<p>
					If you have any questions about these Terms, please contact us at:
				</p>
				<div class="contact-info">
					<p><strong>Heyweek</strong></p>
					<p>Email: <a href="mailto:hello@heyweek.com">hello@heyweek.com</a></p>
					<p>Address: Stockholm, Sweden</p>
					<p>Organization Number: 559411-2345</p>
				</div>
			</section>
		</div>
	</div>
</main>

<Footer />

<style>
	.legal-page {
		padding-block: var(--space-16);
		background: var(--bg-primary);
		min-height: 100vh;
	}
	
	.container {
		max-width: 800px;
		margin-inline: auto;
		padding-inline: var(--container-padding);
	}
	
	.legal-header {
		text-align: center;
		padding-bottom: var(--space-12);
		border-bottom: 1px solid var(--border-light);
		margin-bottom: var(--space-12);
	}
	
	.legal-title {
		font-size: var(--font-size-4xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}
	
	.legal-meta {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
	}
	
	.legal-content {
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
	}
	
	.legal-section {
		margin-bottom: var(--space-12);
	}
	
	.legal-section h2 {
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		padding-top: var(--space-4);
	}
	
	.legal-section h3 {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-top: var(--space-6);
		margin-bottom: var(--space-3);
	}
	
	.legal-section p {
		margin-bottom: var(--space-4);
	}
	
	.legal-section ul {
		margin-left: var(--space-6);
		margin-bottom: var(--space-4);
		list-style: disc;
	}
	
	.legal-section li {
		margin-bottom: var(--space-2);
		line-height: var(--line-height-relaxed);
	}
	
	.legal-section a {
		color: var(--text-brand);
		text-decoration: underline;
		text-underline-offset: var(--text-underline-offset);
		transition: var(--transition-colors);
	}
	
	.legal-section a:hover {
		color: var(--brand-dark);
	}
	
	.intro {
		font-size: var(--font-size-lg);
		color: var(--text-primary);
		margin-bottom: var(--space-6);
	}
	
	.note {
		padding: var(--space-4);
		background: var(--bg-secondary);
		border-left: 3px solid var(--color-warning);
		border-radius: var(--radius-md);
		margin-top: var(--space-4);
		font-size: var(--font-size-sm);
		text-transform: uppercase;
		letter-spacing: var(--letter-spacing-wide);
	}
	
	.contact-info {
		padding: var(--space-6);
		background: var(--bg-secondary);
		border-radius: var(--radius-lg);
		margin-top: var(--space-6);
	}
	
	.contact-info p {
		margin-bottom: var(--space-2);
	}
	
	/* Responsive */
	@media (max-width: 768px) {
		.legal-title {
			font-size: var(--font-size-3xl);
		}
		
		.legal-section h2 {
			font-size: var(--font-size-xl);
		}
		
		.legal-section ul {
			margin-left: var(--space-4);
		}
	}
</style>